import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import SeoHead from '@/components/layout/SeoHead';
import CTABand from '@/components/sections/CTABand';
import NotFound from '@/pages/NotFound';
import { CITY_PAGES } from '@/data/locations';
import { BRAND, SERVICES } from '@/data/site';
import { buildOrganizationJsonLd, buildFaqJsonLd } from '@/lib/entitySchema';
import { CheckCircle2, MapPin, Phone, ArrowRight } from 'lucide-react';

const SLUG_LOOKUP = CITY_PAGES.reduce((acc, p) => {
  acc[p.slug] = p;
  return acc;
}, {});

const SERVICE_LOOKUP = SERVICES.reduce((acc, s) => {
  acc[s.title] = s.slug;
  return acc;
}, {});

export default function CityPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  const page = SLUG_LOOKUP[slug];

  if (!page) {
    return <NotFound />;
  }

  const canonical = `${BRAND.origin}/${page.slug}`;
  const areaServedExtra = [`${page.city}, IN`, `${page.county}, IN`];

  return (
    <>
      <SeoHead
        title={`${page.h1} | Ohana Property & Transition Services`}
        description={page.metaDescription}
        canonical={canonical}
        jsonLd={buildOrganizationJsonLd({ areaServedExtra })}
        faqJsonLd={buildFaqJsonLd(page.faqs)}
      />

      {/* HERO */}
      <section className="bg-ohana-primary text-ohana-bg" data-testid="city-hero">
        <div className="container-ohana pt-24 pb-20 md:pt-32 md:pb-24">
          <p className="overline !text-ohana-accent inline-flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            {page.county} · {page.state}
          </p>
          <h1
            className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl"
            data-testid="city-h1"
          >
            {page.h1}
          </h1>
          <p className="mt-6 max-w-3xl text-lg md:text-xl text-ohana-bg/85 leading-relaxed">
            {page.intro}
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="btn-primary"
              data-testid="city-cta-quote"
            >
              Get My Free Quote
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <a
              href={`tel:${BRAND.phoneTel}`}
              className="btn-ghost-light"
              data-testid="city-cta-call"
            >
              <Phone className="mr-2 h-4 w-4" />
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* LOCAL CONTEXT */}
      <section className="section-pad bg-ohana-bg" data-testid="city-local-context">
        <div className="container-ohana grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline">Why {page.city}</p>
            <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">
              Local {page.service.toLowerCase()} from a partner that actually lives here.
            </h2>
            <p className="mt-5 text-ohana-text-muted text-base md:text-lg leading-relaxed">
              Ohana Property &amp; Transition Services is a Columbus, Indiana company. Woman-owned, Latina-owned, MBE and WBE certified. We treat every {page.city} job like a neighbor&rsquo;s job.
            </p>
          </div>

          <div className="lg:col-span-7">
            <ul className="space-y-3">
              {page.localContext.map((point) => (
                <li
                  key={point}
                  className="flex gap-3 items-start rounded-xl bg-ohana-surface border border-ohana-border px-4 py-3"
                >
                  <CheckCircle2 className="h-5 w-5 text-ohana-primary mt-0.5 flex-none" />
                  <span className="text-sm md:text-base text-ohana-text leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SERVICES IN THIS CITY */}
      <section className="section-pad bg-ohana-surface" data-testid="city-services">
        <div className="container-ohana">
          <div className="max-w-3xl">
            <p className="overline">Services in {page.city}</p>
            <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">
              What we handle in {page.city}, {page.state}.
            </h2>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {page.services.map((title) => {
              const serviceSlug = SERVICE_LOOKUP[title];
              return (
                <article
                  key={title}
                  className="card-default"
                  data-testid={`city-service-${title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <h3 className="font-serif text-xl text-ohana-primary leading-snug">{title}</h3>
                  <p className="mt-3 text-sm text-ohana-text-muted leading-relaxed">
                    Available in {page.city} on our regular schedule, with photo-based quotes and 24 to 48 hour turnaround on most jobs.
                  </p>
                  {serviceSlug && (
                    <Link
                      to={`/${serviceSlug}`}
                      className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-ohana-primary hover:underline"
                      data-testid={`city-service-link-${serviceSlug}`}
                    >
                      Learn about {title}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-ohana-bg" data-testid="city-faq">
        <div className="container-ohana max-w-4xl">
          <p className="overline">Frequently Asked Questions</p>
          <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">
            {page.service} in {page.city}. Questions we hear.
          </h2>

          <div className="mt-10 space-y-4">
            {page.faqs.map((f, idx) => (
              <details
                key={f.q}
                className="rounded-2xl border border-ohana-border bg-white open:bg-ohana-surface group"
                data-testid={`city-faq-${idx}`}
              >
                <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
                  <span className="font-serif text-lg md:text-xl text-ohana-primary leading-snug">
                    {f.q}
                  </span>
                  <span
                    aria-hidden="true"
                    className="grid h-7 w-7 place-items-center rounded-full bg-ohana-primary text-ohana-bg text-sm font-bold group-open:rotate-45 transition"
                  >
                    +
                  </span>
                </summary>
                <div className="px-6 pb-6 -mt-1 text-base text-ohana-text leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* INTERNAL LINKS */}
      <section className="section-pad bg-ohana-surface" data-testid="city-internal-links">
        <div className="container-ohana max-w-4xl">
          <p className="overline">Learn More</p>
          <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">
            About Ohana &amp; how we work.
          </h2>
          <ul className="mt-6 flex flex-wrap gap-3">
            <li>
              <Link
                to="/about"
                className="inline-flex items-center rounded-full border border-ohana-border bg-white px-4 py-2 text-sm font-medium text-ohana-primary hover:bg-ohana-primary hover:text-white transition"
                data-testid="city-link-about"
              >
                About Olga Ortega &amp; Ohana
              </Link>
            </li>
            <li>
              <Link
                to="/hearth-project"
                className="inline-flex items-center rounded-full border border-ohana-border bg-white px-4 py-2 text-sm font-medium text-ohana-primary hover:bg-ohana-primary hover:text-white transition"
                data-testid="city-link-hearth"
              >
                The Hearth Project
              </Link>
            </li>
            <li>
              <Link
                to="/blog"
                className="inline-flex items-center rounded-full border border-ohana-border bg-white px-4 py-2 text-sm font-medium text-ohana-primary hover:bg-ohana-primary hover:text-white transition"
                data-testid="city-link-blog"
              >
                Property Transition Articles
              </Link>
            </li>
          </ul>
        </div>
      </section>

      <CTABand
        title={`Ready for ${page.service.toLowerCase()} in ${page.city}?`}
        subtitle="Send us photos and the property address. We respond same day."
      />
    </>
  );
}
